<template>
  <v-row v-if="isMounted && isFetching">
    <v-col align="center">
      <v-progress-circular
        :size="80"
        color="red-darken-2"
        indeterminate
      ></v-progress-circular>
    </v-col>
  </v-row>

  <template v-if="shownItems && shownItems.length">
    <template v-for="item in shownItems" :key="item.sku ?? item.id">
      <v-row dense>
        <v-col>
          <VariantCard
            :hide-details="hideDetails"
            :hide-info-button="hideInfoButton"
            :hide-share-button="hideShareButton"
            :register-as-link="registerAsLink"
            :value="item"
          />
        </v-col>
      </v-row>
    </template>
  </template>

  <v-row v-if="showAllButton && hasMore">
    <v-col>
      <v-btn color="primary" block size="large" @click="isAllShown = true"
        >Show All</v-btn
      ></v-col
    >
  </v-row>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useMounted } from '@vueuse/core'
import {
  FILTER_DELIVERY_METHOD_ALL,
  FILTER_METHODOLOGY_ALL,
  FILTER_OCCASION_TYPE_ALL,
  FILTER_WHAT_ALL,
  FILTER_WHO_ALL
} from '#root/lib/consts'
import type { VariantFeedItem } from '#root/types/variants'
import { useVariantsStoreOrThrow } from '#root/lib/useVariantsStore'

type VariantFiltersValue = {
  deliveryMethod?: string
  methodology?: string
  occasionType?: string
  productCodes?: string[]
  what?: string
  who?: string
}

const props = withDefaults(
  defineProps<{
    all?: boolean
    filtersValue?: VariantFiltersValue
    hideDetails?: boolean
    hideInfoButton?: boolean
    hideShareButton?: boolean
    limit?: number
    registerAsLink?: boolean
    showAllButton?: boolean
  }>(),
  {
    all: false,
    filtersValue: undefined,
    hideDetails: false,
    hideInfoButton: false,
    hideShareButton: false,
    limit: 10000,
    registerAsLink: false,
    showAllButton: false
  }
)

const isAllShown = ref(false)
const isMounted = useMounted()
const { indexes, isFetching, items } = useVariantsStoreOrThrow()

/** All variant indices (no date filter; variants have no dates). */
const allIndices = computed(() => {
  const list = items.value
  if (!list) return []
  return list.map((_, i) => i)
})

const filteredItems = computed(() => {
  const filters = props.filtersValue || {}
  let indices = allIndices.value

  if (!(indexes.value && items.value && indices.length)) return []

  if (
    typeof filters.deliveryMethod === 'string' &&
    filters.deliveryMethod !== FILTER_DELIVERY_METHOD_ALL
  ) {
    const ind =
      (indexes.value.delivery_method_code &&
        indexes.value.delivery_method_code[
          filters.deliveryMethod.toUpperCase()
        ]) ||
      []
    indices = indices.filter(i => ind.includes(i))
  }

  if (
    typeof filters.methodology === 'string' &&
    filters.methodology !== FILTER_METHODOLOGY_ALL
  ) {
    const ind =
      (indexes.value.methodology_code &&
        indexes.value.methodology_code[filters.methodology.toUpperCase()]) ||
      []
    indices = indices.filter(i => ind.includes(i))
  }

  if (
    typeof filters.occasionType === 'string' &&
    filters.occasionType !== FILTER_OCCASION_TYPE_ALL
  ) {
    const ind =
      (indexes.value.occasion_type_code &&
        indexes.value.occasion_type_code[filters.occasionType.toUpperCase()]) ||
      []
    indices = indices.filter(i => ind.includes(i))
  }

  if (Array.isArray(filters.productCodes) && filters.productCodes.length) {
    let ind: number[] = []
    filters.productCodes.forEach(value => {
      const productCodeIndex =
        indexes.value && indexes.value.product_code
          ? indexes.value.product_code[value.toUpperCase()] || []
          : []
      ind = ind.concat(productCodeIndex)
    })
    indices = indices.filter(i => ind.includes(i))
  }

  if (typeof filters.what === 'string' && filters.what !== FILTER_WHAT_ALL) {
    let ind: number[] = []
    filters.what.split(',').forEach(value => {
      const productCodeIndex =
        indexes.value && indexes.value.product_code
          ? indexes.value.product_code[value.toUpperCase()] || []
          : []
      ind = ind.concat(productCodeIndex)
    })
    indices = indices.filter(i => ind.includes(i))
  }

  if (typeof filters.who === 'string' && filters.who !== FILTER_WHO_ALL) {
    const ind =
      indexes.value && indexes.value.person_code
        ? indexes.value.person_code[filters.who.toUpperCase()] || []
        : []
    indices = indices.filter(i => ind.includes(i))
  }

  const result: VariantFeedItem[] = []
  for (const i of indices) {
    const item = items.value![i]
    if (item != null) result.push(item)
  }
  return result
})

const shownItems = computed(() =>
  props.all || isAllShown.value
    ? filteredItems.value
    : filteredItems.value.slice(0, props.limit)
)

const hasMore = computed(
  () =>
    filteredItems.value &&
    shownItems.value &&
    filteredItems.value.length > shownItems.value.length
)

defineExpose({
  shownItems
})
</script>
